// ============================================================================
// Notifications — a searchable list of notifications that DID NOT send (the same
// set the backend failure-report email covers: PENDING / RETRIED / FAILED /
// UNCERTAIN). Lets the team scan what failed and the reason, so they can
// manually re-send. Self-contained module. Backend: /apiV1/notifications/failures/.
// ============================================================================
import { BellOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, Select, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./index.module.scss";
import {
  getNotificationFailureSummary,
  listNotificationFailures,
  NotificationFailureRow,
  NotificationFailureSummary,
} from "./notifications.api";

const { RangePicker } = DatePicker;

const PAGE_SIZE = 25;

const dash = <span style={{ color: "#bbb" }}>—</span>;

// The non-terminal / failed statuses this dashboard surfaces, with a colour each.
const STATUS_META: Record<string, { color: string; label: string }> = {
  FAILED: { color: "red", label: "Failed" },
  UNDELIVERED: { color: "volcano", label: "Undelivered" }, // provider accepted it but it never reached the receiver
  RETRIED: { color: "orange", label: "Retried" },
  PENDING: { color: "gold", label: "Pending" },
  UNCERTAIN: { color: "purple", label: "Uncertain" },
};

// The channels notifications can be dispatched over (see NotificationManager.available_channels).
const CHANNEL_OPTIONS = [
  "InteraktChannel",
  "InteraktDirectMessageChannel",
  "InteraktImageChannel",
  "InteraktListChannel",
  "InteraktTemplateChannel",
  "PeriskopeDirectMessageChannel",
  "PeriskopeGroupMessageChannel",
  "EmailChannel",
].map((c) => ({ label: c, value: c }));

const fmtDateTime = (v: string | null) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const StatusTag: React.FC<{ status: string }> = ({ status }) => {
  const meta = STATUS_META[status] || { color: "default", label: status };
  return <Tag color={meta.color}>{meta.label}</Tag>;
};

const NotificationsDashboard: React.FC = () => {
  const [rows, setRows] = useState<NotificationFailureRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters.
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [channel, setChannel] = useState<string | undefined>(undefined);
  const [type, setType] = useState<string | undefined>(undefined);
  // Date range on created_at — defaults to today..today (today's failures).
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([dayjs(), dayjs()]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [summary, setSummary] = useState<NotificationFailureSummary | undefined>();

  // Debounced type-to-search over receiver / reason / type.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch((prev) => {
        const next = searchInput.trim();
        if (next !== prev) setPage(1);
        return next;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listNotificationFailures({
      status,
      channel,
      notification_type: type,
      date_from: dateRange[0].format("YYYY-MM-DD"),
      date_to: dateRange[1].format("YYYY-MM-DD"),
      search: search || undefined,
      page,
      page_size: PAGE_SIZE,
      ordering: "-updated_at",
    });
    if (res) {
      setRows(res.results);
      setCount(res.count);
    }
    setLoading(false);
  }, [status, channel, type, dateRange, search, page]);

  const loadSummary = useCallback(async () => {
    const s = await getNotificationFailureSummary({
      date_from: dateRange[0].format("YYYY-MM-DD"),
      date_to: dateRange[1].format("YYYY-MM-DD"),
    });
    setSummary(s);
  }, [dateRange]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const refresh = () => {
    load();
    loadSummary();
  };

  const typeOptions = (summary?.by_type || [])
    .filter((t) => t.notification_type)
    .map((t) => ({ label: `${t.notification_type} (${t.n})`, value: t.notification_type as string }));

  const columns: ColumnsType<NotificationFailureRow> = [
    {
      title: "Status",
      dataIndex: "status",
      width: 110,
      fixed: "left",
      render: (s) => <StatusTag status={s} />,
    },
    {
      title: "Type",
      dataIndex: "notification_type",
      width: 220,
      render: (v) => (v ? <strong>{v}</strong> : dash),
    },
    { title: "Channel", dataIndex: "channel", width: 210, render: (v) => v || dash },
    {
      title: "Receiver",
      dataIndex: "receiver_name",
      width: 220,
      render: (_, row) => {
        const name = row.receiver_name;
        const contact = row.receiver_contact;
        if (!name && !contact) {
          // Fall back to the raw id when the payload had no name/contact.
          return row.receiver_id ? (
            <span style={{ color: "#8c8c8c" }}>
              {row.receiver_type ? `${row.receiver_type}: ` : ""}
              {row.receiver_id}
            </span>
          ) : (
            dash
          );
        }
        return (
          <span>
            {name ? <div>{name}</div> : null}
            {contact ? <div style={{ color: "#8c8c8c", fontSize: 12 }}>{contact}</div> : null}
          </span>
        );
      },
    },
    {
      title: "Reason",
      dataIndex: "remark",
      width: 440,
      render: (v) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            <span className={styles.remark}>{v}</span>
          </Tooltip>
        ) : (
          dash
        ),
    },
    { title: "Created", dataIndex: "created_at", width: 150, render: fmtDateTime },
    { title: "Last update", dataIndex: "updated_at", width: 150, render: fmtDateTime },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BellOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>Notifications — Not Sent</h2>
      </div>



      <div className={styles.summary}>
        <span style={{ fontWeight: 600 }}>Total not sent: {summary?.total ?? "—"}</span>
        {summary &&
          Object.entries(summary.by_status).map(([s, n]) => (
            <Tag key={s} color={STATUS_META[s]?.color || "default"}>
              {STATUS_META[s]?.label || s}: {n}
            </Tag>
          ))}
      </div>

      <Space wrap align="end" style={{ marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Search</div>
          <Input.Search
            placeholder="Receiver, reason, type, lead id, PO no…"
            allowClear
            style={{ width: 280 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Status</div>
          <Select
            allowClear
            placeholder="All"
            style={{ width: 150 }}
            value={status}
            onChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
            options={Object.keys(STATUS_META).map((s) => ({ label: STATUS_META[s].label, value: s }))}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Channel</div>
          <Select
            allowClear
            showSearch
            placeholder="All"
            style={{ width: 220 }}
            value={channel}
            onChange={(v) => {
              setPage(1);
              setChannel(v);
            }}
            options={CHANNEL_OPTIONS}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Type</div>
          <Select
            allowClear
            showSearch
            placeholder="All"
            style={{ width: 240 }}
            value={type}
            onChange={(v) => {
              setPage(1);
              setType(v);
            }}
            options={typeOptions}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#8c8c8c", marginBottom: 2 }}>Date</div>
          <RangePicker
            allowClear={false}
            format="DD MMM YYYY"
            value={dateRange}
            onChange={(vals) => {
              if (vals && vals[0] && vals[1]) {
                setPage(1);
                setDateRange([vals[0], vals[1]]);
              }
            }}
          />
        </div>
        <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
          Refresh
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: count,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </div>
  );
};

export default NotificationsDashboard;
